import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Tooltip, ZoomControl, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { Crosshair, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { RARITY_COLORS, TRAIL, artFor, isPainted } from "@/lib/trolls";
import { TrollArt } from "@/components/landing/TrollArt";
import { StoreLink } from "@/components/landing/StoreLinks";

const BOUNDS = L.latLngBounds(TRAIL.line);
const FIT = { paddingTopLeft: [28, 84], paddingBottomRight: [28, 24] };

const pinIcon = (t) =>
  L.divIcon({
    className: "",
    html: `<div class="tfj-pin${t.category !== "stream" ? " tfj-pin-special" : ""}" style="background:${RARITY_COLORS[t.rarity]}"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

// Painted trolls stand on the trail as little figures, some bigger, some smaller.
const figureHeight = (t, scale) => {
  const base = t.rarity === "legendary" ? 70 : 50;
  return Math.round((base + ((Number(t.id.slice(1)) * 37) % 16)) * scale);
};

const figureIcon = (t, scale, revealed, delay) => {
  const h = figureHeight(t, scale);
  const w = Math.round(h * 0.7);
  return L.divIcon({
    className: "",
    html: `<div class="tfj-figure ${revealed ? "tfj-pop" : "tfj-wait"}" style="width:${w}px;height:${h}px;--d:${delay}ms"><img src="${artFor(t).sm}" alt="${t.name}" /></div>`,
    iconSize: [w, h],
    iconAnchor: [w / 2, h - 3],
  });
};

const flagIcon = (label, color) =>
  L.divIcon({
    className: "",
    html: `<div class="tfj-flag" style="background:${color}">${label}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });

const fallIcon = L.divIcon({ className: "", html: '<div class="tfj-fall">≈</div>', iconSize: [20, 20], iconAnchor: [10, 10] });

function ClusterLayer({ trolls, onSelect }) {
  const map = useMap();
  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 42,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: (c) =>
        L.divIcon({ className: "", html: `<div class="tfj-cluster">${c.getChildCount()}</div>`, iconSize: [40, 40] }),
    });
    trolls.forEach((t) => {
      const m = L.marker([t.lat, t.lng], { icon: pinIcon(t), title: t.name, alt: t.name });
      m.on("click", () => onSelect(t));
      group.addLayer(m);
    });
    map.addLayer(group);
    return () => map.removeLayer(group);
  }, [map, trolls, onSelect]);
  return null;
}

const Recenter = ({ label }) => {
  const map = useMap();
  return (
    <button
      type="button"
      data-testid="map-recenter-button"
      onClick={() => map.flyToBounds(BOUNDS, { ...FIT, duration: 0.8 })}
      className="absolute z-[500] right-4 top-4 inline-flex items-center gap-2 rounded-full bg-parchment/95 border border-line px-4 py-2 text-sm font-extrabold text-ink shadow-journal hover:bg-moss hover:text-parchment transition-colors"
    >
      <Crosshair className="w-4 h-4" /> {label}
    </button>
  );
};

const TrollCard = ({ troll, onClose }) => {
  const { t, tl } = useLang();
  return (
    <div data-testid="map-troll-card" className="absolute z-[500] left-3 right-3 bottom-3 sm:left-4 sm:right-auto sm:w-[380px] journal-card bg-parchment p-4 flex gap-4 items-end">
      <TrollArt troll={troll} small className="h-28 w-20 shrink-0" />
      <div className="min-w-0 flex-1 pb-1">
        <div className="text-[11px] font-extrabold uppercase tracking-[0.16em]" style={{ color: RARITY_COLORS[troll.rarity] }}>
          {t.rarity[troll.rarity]} · {troll.xp} XP
        </div>
        <div data-testid="map-troll-card-name" className="font-display text-xl font-semibold text-ink leading-tight mt-0.5">{troll.name}</div>
        <div className="text-sm font-bold text-rowan">{tl(troll, "epithet")}</div>
        <div className="text-xs text-ink-muted mt-1">{troll.route_segment} · {t.category[troll.category]}</div>
        <StoreLink testId="map-troll-card-cta" className="inline-flex mt-3 rounded-full bg-moss text-parchment px-4 py-2 text-xs font-extrabold hover:bg-moss-dark transition-colors">
          {t.map.catchIt}
        </StoreLink>
      </div>
      <button type="button" onClick={onClose} aria-label={t.map.close} data-testid="map-troll-card-close" className="self-start text-ink-muted hover:text-ink">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Flips to true once the map is well inside the viewport (or right away for reduced motion).
function useRevealOnView(ref) {
  const [revealed, setRevealed] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    if (revealed || !ref.current) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, revealed]);
  return revealed;
}

export default function TrollMap({ trolls }) {
  const { t, lang } = useLang();
  const [selected, setSelected] = useState(null);
  const boxRef = useRef(null);
  const onSelect = useCallback((tr) => setSelected(tr), []);
  const icons = useMemo(() => ({ start: flagIcon("▲", "#6D7C5E"), end: flagIcon("◆", "#A35C53") }), []);
  const scale = window.innerWidth < 640 ? 0.55 : 1;
  const revealed = useRevealOnView(boxRef);
  const figures = useMemo(() => trolls.filter(isPainted).sort((a, b) => a.km_marker - b.km_marker), [trolls]);
  const figureIcons = useMemo(
    () => new Map(figures.map((tr, i) => [tr.id, figureIcon(tr, scale, revealed, i * 200)])),
    [figures, scale, revealed]
  );
  const pins = useMemo(() => trolls.filter((tr) => !isPainted(tr)), [trolls]);

  return (
    <div ref={boxRef} data-testid="trail-map" data-revealed={revealed} className="relative w-full h-[420px] md:h-[640px] rounded-[20px] overflow-hidden border border-line shadow-journal bg-paper">
      <MapContainer bounds={BOUNDS} boundsOptions={FIT} zoomSnap={0.25} zoomDelta={0.5} scrollWheelZoom={false} className="w-full h-full tfj-map" zoomControl={false}>
        <ZoomControl key={lang} position="topleft" zoomInTitle={t.a11y.zoomIn} zoomOutTitle={t.a11y.zoomOut} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Polyline positions={TRAIL.line} pathOptions={{ color: "#B37A4B", weight: 4, opacity: 0.9, dashArray: "2 9", lineCap: "round" }} />
        {TRAIL.waterfalls.map((w) => (
          <Marker key={w.id} position={[w.lat, w.lng]} icon={fallIcon}>
            <Tooltip direction="top" offset={[0, -8]}>{t.map.waterfall} {w.id} · {w.route_segment}</Tooltip>
          </Marker>
        ))}
        <Marker position={TRAIL.start} icon={icons.start}><Tooltip permanent direction="top" offset={[0, -14]}>{t.map.start}</Tooltip></Marker>
        <Marker position={TRAIL.end} icon={icons.end}><Tooltip permanent direction="top" offset={[0, -14]}>{t.map.finish}</Tooltip></Marker>
        <ClusterLayer trolls={pins} onSelect={onSelect} />
        {figures.map((tr) => (
          <Marker
            key={tr.id}
            position={[tr.lat, tr.lng]}
            icon={figureIcons.get(tr.id)}
            eventHandlers={{ click: () => onSelect(tr) }}
          >
            <Tooltip direction="top" offset={[0, -figureHeight(tr, scale)]}>{tr.name}</Tooltip>
          </Marker>
        ))}
        <Recenter label={t.map.recenter} />
      </MapContainer>
      {selected && <TrollCard troll={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
